export interface OwnerLite extends OwnerEntityMeta {
  id: string;
  full_name: string | null;
  full_name_en: string | null;
}

/**
 * Canonical PostgREST column list for an embedded owner (`owner:owners(...)`).
 * Single source of truth so every property/unit/contract embed surfaces the
 * commercial-entity + representative fields — add a column here, not in six
 * separate select strings. (Resolves the recurring "new owner field silently
 * dropped on a surface whose embed wasn't hand-updated" class of bug.)
 */
export const OWNER_LITE_COLUMNS =
  "id, full_name, full_name_en, owner_type, commercial_name, commercial_name_en, representative_name, representative_name_en";

interface UnitWithOwner {
  owner_id?: string | null;
  owner?: OwnerLite | null;
}

interface PropertyWithOwner {
  owner_id?: string | null;
  owner?: OwnerLite | null;
}

/**
 * Resolve the effective owner of a unit.
 * Multi-owner properties leave property.owner_id null and set unit.owner_id;
 * single-owner properties carry the owner on the property.
 */
export function getEffectiveUnitOwner(
  unit?: UnitWithOwner | null,
  property?: PropertyWithOwner | null,
): OwnerLite | null {
  if (unit?.owner) return unit.owner;
  if (property?.owner) return property.owner;
  return null;
}

export function getOwnerDisplayName(
  owner: OwnerLite | null | undefined,
  language: "ar" | "en" | string,
): string {
  if (!owner) return "";
  // Entity-aware: an organization owner shows its establishment (commercial)
  // name; an individual shows the person name. OwnerLite now carries the
  // commercial/representative meta, so every property/unit/contract surface that
  // calls this resolves identically — no caller re-implements the rule.
  return getOwnerEntityDisplayName(owner, owner, language);
}

/**
 * Entity-type metadata for an owner — enough to distinguish an individual from a
 * commercial establishment and to surface the establishment / representative
 * names, WITHOUT widening the heavier owners-with-stats list shape.
 */
export interface OwnerEntityMeta {
  owner_type?: string | null;
  commercial_name?: string | null;
  commercial_name_en?: string | null;
  representative_name?: string | null;
  representative_name_en?: string | null;
}

/** True when the owner is a commercial establishment (organization). */
export function isOrganizationOwner(meta: OwnerEntityMeta | null | undefined): boolean {
  return meta?.owner_type === "commercial";
}

/**
 * Display name honouring the entity type: for an organization the establishment
 * (commercial) name is primary; otherwise the person's name. Falls back across
 * languages, and to the person name when the establishment name is blank.
 */
export function getOwnerEntityDisplayName(
  base: { full_name?: string | null; full_name_en?: string | null },
  meta: OwnerEntityMeta | null | undefined,
  language: "ar" | "en" | string,
): string {
  const personName =
    language === "ar"
      ? base.full_name || base.full_name_en || ""
      : base.full_name_en || base.full_name || "";
  if (!isOrganizationOwner(meta)) return personName;
  const orgName =
    language === "ar"
      ? meta?.commercial_name || meta?.commercial_name_en || ""
      : meta?.commercial_name_en || meta?.commercial_name || "";
  return orgName || personName;
}

/** The organization's authorised representative name (language-aware), or "". */
export function getOwnerRepresentativeName(
  meta: OwnerEntityMeta | null | undefined,
  language: "ar" | "en" | string,
): string {
  if (!meta) return "";
  return language === "ar"
    ? meta.representative_name || meta.representative_name_en || ""
    : meta.representative_name_en || meta.representative_name || "";
}

/**
 * Resolve the single-line owner label shown on a property card.
 *
 * Rules:
 *   1. If the property carries a direct `owner` (single-owner property), use that name.
 *   2. Otherwise look at the distinct owners across the property's units:
 *      - 0 → null (no label rendered)
 *      - 1 → that owner's name
 *      - >1 → "{n} owners" (bilingual via `t`)
 *
 * Inputs are typed permissively because the embedded objects coming back from
 * Supabase aren't known to the auto-generated row types.
 */
export function getPropertyCardOwnerLabel(
  property: PropertyWithOwner | null | undefined,
  units: ReadonlyArray<UnitWithOwner> | null | undefined,
  t: (ar: string, en: string) => string,
  language: "ar" | "en" | string,
): string | null {
  if (property?.owner) {
    return getOwnerDisplayName(property.owner, language) || null;
  }
  const distinct: Record<string, OwnerLite> = {};
  (units ?? []).forEach((u) => {
    const o = u?.owner;
    if (o?.id) distinct[o.id] = o;
  });
  const owners = Object.values(distinct);
  if (owners.length === 0) return null;
  if (owners.length === 1) return getOwnerDisplayName(owners[0], language) || null;
  return t(`${owners.length} ملاك`, `${owners.length} owners`);
}
